/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 11 de noviembre 2024
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React, { useState } from "react";
import {
  Typography,
  Box,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Fade,
  useTheme,
  useMediaQuery,
  InputAdornment,
  LinearProgress,
  Alert,
  keyframes,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import {
  Lock,
  Visibility,
  VisibilityOff,
  Security,
  CheckCircle,
  ArrowBack,
  Shield,
  VpnKey,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import api from "../../../axiosConfig";
import { esAdministrador, rutaCambioContrasena } from "../../../utils/perfil";
import logo from "../../../Assets/img/logo.webp";

// Animaciones
const floatAnimation = keyframes`
  0%, 100% { 
    transform: translateY(0px);
  }
  50% { 
    transform: translateY(-10px);
  }
`;

const scanAnimation = keyframes`
  0% { left: -100%; }
  100% { left: 100%; }
`;

const successPulse = keyframes`
  0%, 100% { 
    transform: scale(1);
    opacity: 1;
  }
  50% { 
    transform: scale(1.05);
    opacity: 0.9;
  }
`;

const shieldGlow = keyframes`
  0%, 100% { 
    filter: drop-shadow(0 0 15px rgba(255, 255, 255, 0.5));
  }
  50% { 
    filter: drop-shadow(0 0 25px rgba(255, 255, 255, 0.8));
  }
`;

// Contenedor principal con fondo degradado
const ChangePasswordContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'linear-gradient(135deg, #0a2e5c 0%, #051b38 100%)',
  position: 'relative',
  overflow: 'hidden',
  padding: theme.spacing(2),
  backgroundImage: `
    radial-gradient(circle at 20% 80%, rgba(10, 46, 92, 0.4) 0%, transparent 60%),
    radial-gradient(circle at 80% 20%, rgba(10, 46, 92, 0.25) 0%, transparent 60%),
    radial-gradient(circle at 60% 60%, rgba(10, 46, 92, 0.18) 0%, transparent 50%)
  `,
}));

// Logo con efectos
const StyledLogo = styled('img')(({ theme, isMobile }) => ({
  width: isMobile ? 100 : 120,
  marginBottom: theme.spacing(2),
  filter: 'drop-shadow(0 0 15px rgba(10, 46, 92, 0.8))',
  animation: `${floatAnimation} 6s ease-in-out infinite`,
  [theme.breakpoints.down('sm')]: {
    width: 80,
    marginBottom: theme.spacing(1.5),
  },
}));

// Contenedor del formulario con glass effect
const FormContainer = styled(Box)(({ theme, isMobile }) => ({
  background: 'rgba(10, 46, 92, 0.1)',
  backdropFilter: 'blur(20px)',
  border: '2px solid rgba(10, 46, 92, 0.3)',
  padding: isMobile ? theme.spacing(3) : theme.spacing(4),
  borderRadius: 20,
  boxShadow: `
    0 20px 40px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(10, 46, 92, 0.2),
    inset 0 1px 0 rgba(10, 46, 92, 0.3),
    0 0 30px rgba(10, 46, 92, 0.4)
  `,
  position: 'relative',
  overflow: 'hidden',
  maxWidth: isMobile ? '90vw' : 450,
  width: '100%',
  
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(10, 46, 92, 0.2), transparent)',
    animation: `${scanAnimation} 8s linear infinite`,
    zIndex: 0,
    pointerEvents: 'none',
  },
}));

// Input personalizado
const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: 12,
    transition: 'all 0.3s ease',
    position: 'relative',
    zIndex: 10,
    backdropFilter: 'blur(10px)',
    
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
      borderWidth: 2,
    },
    
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
    
    '&.Mui-focused fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.8)',
      boxShadow: '0 0 20px rgba(255, 255, 255, 0.3)',
    },
    
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
    },
    
    '& input': {
      color: '#ffffff',
      '&::placeholder': {
        color: 'rgba(255, 255, 255, 0.7)',
      },
    },
  },
  
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.9)',
    fontWeight: 600,
    zIndex: 15,
    
    '&.Mui-focused': {
      color: '#ffffff',
      fontWeight: 700,
    },
    
    '&.MuiFormLabel-filled': {
      color: 'rgba(255, 255, 255, 0.9)',
    },
  },
  
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: '1.4rem',
    filter: 'drop-shadow(0 0 5px rgba(255, 255, 255, 0.3))',
    transition: 'all 0.3s ease',
  },
  
  '& .MuiOutlinedInput-root.Mui-focused .MuiInputAdornment-root .MuiSvgIcon-root': {
    transform: 'scale(1.1)',
    color: '#ffffff',
    filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.5))',
  },
}));

// Botón principal
const StyledButton = styled(Button)(({ theme, isMobile, variant }) => ({
  width: '100%',
  padding: isMobile ? theme.spacing(1.5) : theme.spacing(2),
  marginTop: theme.spacing(2),
  borderRadius: 12,
  fontSize: isMobile ? '0.9rem' : '1rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 1,
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  zIndex: 10,
  
  ...(variant === 'primary' ? {
    background: 'linear-gradient(135deg, #0a2e5c, #051b38)',
    color: 'white',
    boxShadow: '0 8px 20px rgba(10, 46, 92, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
    
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
      transition: 'left 0.5s ease',
    },
    
    '&:hover::before': {
      left: '100%',
    },
    
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 25px rgba(10, 46, 92, 0.8), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
      background: 'linear-gradient(135deg, #051b38, #082340)',
    },
    
    '&:disabled': {
      background: 'rgba(10, 46, 92, 0.5)',
      transform: 'none',
      '&::before': {
        display: 'none',
      },
    },
  } : {
    border: '2px solid rgba(10, 46, 92, 0.3)',
    color: '#0a2e5c',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    
    '&:hover': {
      backgroundColor: 'rgba(10, 46, 92, 0.05)',
      borderColor: '#0a2e5c',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 15px rgba(10, 46, 92, 0.2)',
    },
  }),
  
  '&:active': {
    transform: 'translateY(0)',
  },
}));

// Título con iconos
const StyledTitle = styled(Typography)(({ theme, isMobile }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  color: '#ffffff',
  fontSize: isMobile ? '1.4rem' : '1.8rem',
  fontWeight: 700,
  position: 'relative',
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  textShadow: '0 0 10px rgba(255, 255, 255, 0.3)',
}));

// Descripción
const StyledDescription = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(3),
  color: 'rgba(255, 255, 255, 0.8)',
  fontSize: '0.9rem',
  lineHeight: 1.5,
  position: 'relative',
  zIndex: 10,
}));

// Indicador de fortaleza de contraseña
const PasswordStrengthContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  position: 'relative',
  zIndex: 10,
  
  '& .strength-label': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
  },
  
  '& .strength-text': {
    fontSize: '0.75rem',
    color: 'rgba(255, 255, 255, 0.8)',
    fontWeight: 500,
  },
  
  '& .strength-value': {
    fontSize: '0.75rem',
    fontWeight: 700,
  },
}));

// Contenedor de éxito
const SuccessContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  borderRadius: 16,
  background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(67, 160, 71, 0.05))',
  border: '2px solid rgba(76, 175, 80, 0.3)',
  position: 'relative',
  zIndex: 10,
  animation: `${successPulse} 2s ease-in-out infinite`,
  marginBottom: theme.spacing(2),
  
  '& .success-icon': {
    fontSize: '3rem',
    color: '#4caf50',
    marginBottom: theme.spacing(1.5),
    filter: 'drop-shadow(0 0 10px rgba(76, 175, 80, 0.4))',
  },
  
  '& .success-title': {
    color: '#2e7d32',
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  
  '& .success-message': {
    color: '#388e3c',
    fontSize: '0.9rem',
    lineHeight: 1.5,
  },
}));

// Icono de escudo central
const ShieldIcon = styled(Shield)(({ theme }) => ({
  fontSize: '4rem',
  color: 'rgba(255, 255, 255, 0.9)',
  marginBottom: theme.spacing(2),
  filter: 'drop-shadow(0 0 15px rgba(255, 255, 255, 0.5))',
  animation: `${shieldGlow} 3s ease-in-out infinite`,
  position: 'relative',
  zIndex: 10,
}));

// Componente para el indicador de seguridad de la contraseña
const PasswordStrengthIndicator = ({ password }) => {
  const calculateStrength = () => {
    if (!password) return { strength: 0, text: '', color: '' };
    
    let score = 0;
    
    // Longitud
    if (password.length >= 6) score += 20;
    if (password.length >= 8) score += 15;
    if (password.length >= 12) score += 15;
    
    // Complejidad
    if (/[a-z]/.test(password)) score += 10;
    if (/[A-Z]/.test(password)) score += 10;
    if (/\d/.test(password)) score += 10;
    if (/[^A-Za-z0-9]/.test(password)) score += 20;
    
    const strength = Math.min(100, score);
    
    if (strength < 40) return { strength, text: 'Muy débil', color: '#f44336' };
    if (strength < 60) return { strength, text: 'Débil', color: '#ff9800' };
    if (strength < 80) return { strength, text: 'Buena', color: '#2196f3' };
    return { strength, text: 'Muy fuerte', color: '#4caf50' };
  };

  const { strength, text, color } = calculateStrength();

  if (!password) return null;

  return (
    <PasswordStrengthContainer>
      <Box className="strength-label">
        <Typography className="strength-text">
          Seguridad de la contraseña
        </Typography>
        <Typography className="strength-value" sx={{ color }}>
          {text}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={strength}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: 'rgba(0,0,0,0.1)',
          '& .MuiLinearProgress-bar': {
            backgroundColor: color,
            borderRadius: 4,
          }
        }}
      />
    </PasswordStrengthContainer>
  );
};

function ChangePassword() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (error) setError("");
    if (success) setSuccess("");
  };

  const validatePasswordForm = () => {
    if (!passwordData.currentPassword) {
      setError("La contraseña actual es requerida");
      return false;
    }
    if (!passwordData.newPassword) {
      setError("La nueva contraseña es requerida");
      return false;
    }
    if (passwordData.newPassword.length < 6) {
      setError("La nueva contraseña debe tener al menos 6 caracteres");
      return false;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError("Las contraseñas no coinciden");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validatePasswordForm()) {
      return;
    }

    setLoading(true);
    try {
      await api.post(esAdministrador() ? rutaCambioContrasena() : "/auth/conductor/cambiar-password", {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      setSuccess("¡Contraseña actualizada exitosamente!");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      // Redirigir después de 3 segundos
      setTimeout(() => {
        navigate("/modules/Dashboard");
      }, 3000);
      
    } catch (error) {
      if (error.response) {
        setError(
          error.response.data.message || "Error al cambiar la contraseña"
        );
      } else {
        setError("Error de conexión al servidor");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/modules/Dashboard");
  };

  return (
    <ChangePasswordContainer>
      <StyledLogo src={logo} alt="Mogotax Logo" isMobile={isMobile} />
      
      <FormContainer isMobile={isMobile}>
        <StyledButton
          variant="secondary"
          startIcon={<ArrowBack />}
          onClick={handleBack}
          isMobile={isMobile}
          sx={{ mb: 3, textTransform: 'none', justifyContent: 'flex-start' }}
        >
          Volver al Inicio
        </StyledButton>

        <Box sx={{ textAlign: 'center', mb: 3, position: 'relative', zIndex: 10 }}>
          <ShieldIcon />
          <StyledTitle variant="h4" isMobile={isMobile}>
            <VpnKey sx={{ fontSize: isMobile ? '1.4rem' : '1.8rem' }} />
            Cambiar Contraseña
          </StyledTitle>
          <StyledDescription>
            Actualiza tu contraseña para mantener tu cuenta segura y protegida
          </StyledDescription>
        </Box>

        {success ? (
          <SuccessContainer>
            <CheckCircle className="success-icon" />
            <Typography variant="h6" className="success-title">
              ¡Contraseña actualizada exitosamente!
            </Typography>
            <Typography className="success-message">
              Tu contraseña ha sido cambiada correctamente. 
              Serás redirigido al dashboard en unos segundos.
            </Typography>
            <LinearProgress 
              sx={{ 
                mt: 2, 
                borderRadius: 2,
                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#4caf50'
                }
              }} 
            />
          </SuccessContainer>
        ) : (
          <>
            {error && (
              <Fade in={Boolean(error)}>
                <Alert 
                  severity="error"
                  sx={{ 
                    mb: 2,
                    borderRadius: 2,
                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                    border: '1px solid rgba(244, 67, 54, 0.3)',
                    position: 'relative',
                    zIndex: 10,
                  }}
                >
                  {error}
                </Alert>
              </Fade>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ position: 'relative', zIndex: 10 }}>
              <StyledTextField
                fullWidth
                label="Contraseña Actual"
                type={showPassword.currentPassword ? "text" : "password"}
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Security />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => togglePasswordVisibility("currentPassword")}
                        edge="end"
                        size="small"
                        sx={{ color: '#0a2e5c' }}
                      >
                        {showPassword.currentPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <StyledTextField
                fullWidth
                label="Nueva Contraseña"
                type={showPassword.newPassword ? "text" : "password"}
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => togglePasswordVisibility("newPassword")}
                        edge="end"
                        size="small"
                        sx={{ color: '#0a2e5c' }}
                      >
                        {showPassword.newPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {passwordData.newPassword && (
                <PasswordStrengthIndicator password={passwordData.newPassword} />
              )}

              <StyledTextField
                fullWidth
                label="Confirmar Nueva Contraseña"
                type={showPassword.confirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => togglePasswordVisibility("confirmPassword")}
                        edge="end"
                        size="small"
                        sx={{ color: '#0a2e5c' }}
                      >
                        {showPassword.confirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {passwordData.confirmPassword && 
               passwordData.newPassword !== passwordData.confirmPassword && (
                <Alert 
                  severity="warning" 
                  sx={{ 
                    mb: 2,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    border: '1px solid rgba(255, 152, 0, 0.3)',
                    position: 'relative',
                    zIndex: 10,
                  }}
                >
                  Las contraseñas no coinciden
                </Alert>
              )}

              <StyledButton
                type="submit"
                variant="primary"
                disabled={loading}
                isMobile={isMobile}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    Actualizando contraseña...
                  </Box>
                ) : (
                  'Actualizar Contraseña'
                )}
              </StyledButton>
            </Box>
          </>
        )}
      </FormContainer>
    </ChangePasswordContainer>
  );
}

export default ChangePassword;
